import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { 
  Building, 
  Calendar, 
  FileText, 
  Target, 
  CheckCircle, 
  Zap,
  Download,
  Eye,
  Plus,
  Library,
  Trash2,
  BarChart3,
  ArrowLeft
} from "lucide-react";
import { JobLibraryService } from "@/services/jobLibraryService";
import { useToast } from "@/hooks/use-toast";
import { JobAnalysisDetail } from "./JobAnalysisDetail";

interface JobLibraryProps {
  savedJobs: SavedJobDescription[];
  onUploadNew: () => void;
  onBack?: () => void;
  onJobDeleted?: () => void;
}

interface SavedJobDescription {
  id: string;
  companyName: string;
  jobTitle: string;
  companyMission: string;
  selectedResponsibilities: string[];
  selectedRequirements: string[];
  selectedSkills: string[];
  allResponsibilities: string[];
  allRequirements: string[];
  allSkills: string[];
  matchScore: number;
  dateCreated: string;
}

export function JobLibrary({ savedJobs, onUploadNew, onBack, onJobDeleted }: JobLibraryProps) {
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<SavedJobDescription | null>(null);
  const [viewMode, setViewMode] = useState<'personalized' | 'complete' | 'excluded'>('personalized');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [showAnalysisDetail, setShowAnalysisDetail] = useState<SavedJobDescription | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getExcludedItems = (job: SavedJobDescription) => {
    return {
      responsibilities: job.allResponsibilities.filter(r => !job.selectedResponsibilities.includes(r)),
      requirements: job.allRequirements.filter(r => !job.selectedRequirements.includes(r)),
      skills: job.allSkills.filter(s => !job.selectedSkills.includes(s))
    };
  };

  const handleSelectJob = (jobId: string, checked: boolean) => {
    const newSelected = new Set(selectedJobs);
    if (checked) {
      newSelected.add(jobId);
    } else {
      newSelected.delete(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedJobs(new Set(savedJobs.map(job => job.id)));
    } else {
      setSelectedJobs(new Set());
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const deletePromises = Array.from(selectedJobs).map(jobId => 
        JobLibraryService.deleteJob(jobId)
      );
      
      await Promise.all(deletePromises);
      
      toast({
        title: "Jobs Deleted",
        description: `${selectedJobs.size} job description(s) have been removed from your library.`
      });
      
      // Clear selections
      setSelectedJobs(new Set());
      setShowBulkDeleteDialog(false);
      
      // Clear selected job if it was deleted
      if (selectedJob && selectedJobs.has(selectedJob.id)) {
        setSelectedJob(null);
      }
      
      // Refresh the library
      onJobDeleted?.();
      
    } catch (error) {
      console.error('Error deleting jobs:', error);
      toast({
        title: "Bulk Delete Failed",
        description: "Failed to delete some job descriptions. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteJob = async (jobId: string, jobTitle: string) => {
    if (!confirm(`Are you sure you want to delete "${jobTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsDeleting(jobId);
      await JobLibraryService.deleteJob(jobId);
      
      toast({
        title: "Job Deleted",
        description: "Job description has been removed from your library."
      });
      
      // Clear selection if the deleted job was selected
      if (selectedJob?.id === jobId) {
        setSelectedJob(null);
      }
      
      // Remove from selected jobs if it was selected
      const newSelected = new Set(selectedJobs);
      newSelected.delete(jobId);
      setSelectedJobs(newSelected);
      
      // Refresh the library
      onJobDeleted?.();
      
    } catch (error) {
      console.error('Error deleting job:', error);
      toast({
        title: "Delete Failed",
        description: error instanceof Error ? error.message : "Failed to delete job description",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const renderDocumentView = (job: SavedJobDescription, mode: string) => {
    const excluded = getExcludedItems(job);

    if (mode === 'personalized') {
      const getScoreColor = (score: number) => {
        if (score >= 80) return "text-green-600 border-green-600 bg-green-50";
        if (score >= 56) return "text-yellow-600 border-yellow-600 bg-yellow-50";
        if (score >= 30) return "text-orange-600 border-orange-600 bg-orange-50";
        return "text-red-600 border-red-600 bg-red-50";
      };

      const getScoreLabel = (score: number) => {
        if (score >= 80) return "Excellent Match";
        if (score >= 56) return "Good Match";
        if (score >= 30) return "Fair Match";
        return "Poor Match";
      };

      return (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800 font-medium">Personalized Job Description</p>
            <p className="text-xs text-blue-600">This shows only the requirements and responsibilities most relevant to your profile</p>
          </div>

          {/* Company Info */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">{job.jobTitle}</h2>
            <h3 className="text-xl text-primary">{job.companyName}</h3>
            <p className="text-foreground leading-relaxed">{job.companyMission}</p>
          </div>

          <Separator />

          {/* Selected Content */}
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Key Responsibilities ({job.selectedResponsibilities.length})
              </h4>
              <ul className="space-y-2">
                {job.selectedResponsibilities.map((resp, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span className="text-sm leading-relaxed">{resp}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                Requirements & Qualifications ({job.selectedRequirements.length})
              </h4>
              <ul className="space-y-2">
                {job.selectedRequirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span className="text-sm leading-relaxed">{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-success" />
                Key Skills & Technologies ({job.selectedSkills.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {job.selectedSkills.map((skill, index) => (
                  <Badge key={index} variant="outline" className="bg-success-light border-success/30">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === 'complete') {
      return (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800 font-medium">Complete Modified Job Description</p>
            <p className="text-xs text-blue-600">This is the clean, comprehensive version used for resume analysis</p>
          </div>

          {/* Company Info */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">{job.jobTitle}</h2>
            <h3 className="text-xl text-primary">{job.companyName}</h3>
            <p className="text-foreground leading-relaxed">{job.companyMission}</p>
          </div>

          <Separator />

          {/* All Content - Clean Format */}
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Key Responsibilities ({job.allResponsibilities.length})
              </h4>
              <ul className="space-y-2">
                {job.allResponsibilities.map((resp, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span className="text-sm leading-relaxed">{resp}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                Requirements & Qualifications ({job.allRequirements.length})
              </h4>
              <ul className="space-y-2">
                {job.allRequirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span className="text-sm leading-relaxed">{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-success" />
                Key Skills & Technologies ({job.allSkills.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {job.allSkills.map((skill, index) => (
                  <Badge 
                    key={index} 
                    variant="outline" 
                    className="bg-success-light border-success/30"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (mode === 'excluded') {
      return (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800 font-medium">Excluded Job Requirements</p>
            <p className="text-xs text-blue-600">These items were not selected for your personalized job description</p>
          </div>

          {/* Company Info */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">{job.jobTitle}</h2>
            <h3 className="text-xl text-primary">{job.companyName}</h3>
            <p className="text-foreground leading-relaxed">{job.companyMission}</p>
          </div>

          <Separator />

          {/* Excluded Content */}
          <div className="space-y-6">
            {excluded.responsibilities.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5 text-muted-foreground" />
                  Excluded Responsibilities ({excluded.responsibilities.length})
                </h4>
                <ul className="space-y-2">
                  {excluded.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-muted-foreground mt-1">•</span>
                      <span className="text-sm leading-relaxed text-muted-foreground">{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {excluded.requirements.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-muted-foreground" />
                  Excluded Requirements ({excluded.requirements.length})
                </h4>
                <ul className="space-y-2">
                  {excluded.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-muted-foreground mt-1">•</span>
                      <span className="text-sm leading-relaxed text-muted-foreground">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {excluded.skills.length > 0 && (
              <div>
                <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-muted-foreground" />
                  Excluded Skills ({excluded.skills.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {excluded.skills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="bg-muted text-muted-foreground border-muted">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {excluded.responsibilities.length === 0 && excluded.requirements.length === 0 && excluded.skills.length === 0 && (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No items were excluded from this job description.</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onBack || (() => navigate('/'))}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Job Library</h1>
                  <p className="text-sm text-muted-foreground">Manage your saved job descriptions and analysis results</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button onClick={onUploadNew} className="bg-gradient-to-r from-primary to-accent">
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Job
                </Button>
                <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                  <span className="text-xs font-medium text-muted-foreground">powered by</span>
                  <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-6xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight relative">
                    <div className="absolute inset-0 bg-background/60 backdrop-blur-md rounded-lg shadow-lg -z-10"></div>
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Saved Jobs{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Library
                    </span>
                  </h1>
                  
                  <div className="max-w-3xl mx-auto px-4 mt-4 relative">
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-md rounded-lg shadow-md -z-10"></div>
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      Your Curated Collection of Job Descriptions & Analysis Results
                    </p>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="space-y-6">
                <div className="container mx-auto max-w-7xl">
                  {selectedJob ? (
                    /* Job Detail View - Full Width */
                    <div className="max-w-5xl mx-auto">
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-36 h-36 rounded-full border-4 flex flex-col items-center justify-center ${selectedJob.matchScore >= 80 ? "text-green-600 border-green-600 bg-green-50" : selectedJob.matchScore >= 56 ? "text-yellow-600 border-yellow-600 bg-yellow-50" : selectedJob.matchScore >= 30 ? "text-orange-600 border-orange-600 bg-orange-50" : "text-red-600 border-red-600 bg-red-50"}`}>
                                <div className="text-4xl font-bold">{selectedJob.matchScore}%</div>
                                <div className="text-sm font-medium">
                                  {selectedJob.matchScore >= 80 ? "Excellent Match" : selectedJob.matchScore >= 56 ? "Good Match" : selectedJob.matchScore >= 30 ? "Fair Match" : "Poor Match"}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button
                                  onClick={() => setShowAnalysisDetail(selectedJob)}
                                  size="sm"
                                  className="bg-primary hover:bg-primary/90 text-white font-semibold px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-200"
                                >
                                  <BarChart3 className="w-4 h-4 mr-2" />
                                  View Full Qualifications Report
                                </Button>
                                <p className="text-base text-black font-bold text-center">
                                  Congrats! You are qualified to apply for this job!
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" onClick={() => setSelectedJob(null)}>
                                ← Back to Library
                              </Button>
                              <Button variant="outline" size="sm">
                                <Download className="w-4 h-4" />
                                Export
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="mb-4">
                            <h4 className="text-lg font-semibold text-center mb-2">Choose 1 of 3 Job Description Viewing Options</h4>
                            <p className="text-sm text-muted-foreground text-center">Each option shows the same job in a different format to support your tailoring strategy</p>
                          </div>
                          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as any)}>
                            <TabsList className="grid w-full grid-cols-3 mb-6 bg-transparent p-2 gap-2 h-auto">
                              <TabsTrigger 
                                value="personalized" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md border-2 border-muted-foreground/30 bg-background hover:border-primary/50 hover:bg-muted/30 font-medium transition-all py-3 px-4 rounded-lg"
                              >
                                Personalized JD
                              </TabsTrigger>
                              <TabsTrigger 
                                value="complete"
                                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md border-2 border-muted-foreground/30 bg-background hover:border-primary/50 hover:bg-muted/30 font-medium transition-all py-3 px-4 rounded-lg"
                              >
                                Complete JD
                              </TabsTrigger>
                              <TabsTrigger 
                                value="excluded"
                                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-md border-2 border-muted-foreground/30 bg-background hover:border-primary/50 hover:bg-muted/30 font-medium transition-all py-3 px-4 rounded-lg"
                              >
                                Exclude Details JD
                              </TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="personalized" className="mt-0">
                              {renderDocumentView(selectedJob, 'personalized')}
                            </TabsContent>
                            
                            <TabsContent value="complete" className="mt-0">
                              {renderDocumentView(selectedJob, 'complete')}
                            </TabsContent>
                            
                            <TabsContent value="excluded" className="mt-0">
                              {renderDocumentView(selectedJob, 'excluded')}
                            </TabsContent>
                          </Tabs>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    /* Job List View - Full Width */
                    <div className="max-w-6xl mx-auto">
                      <Card>
                        <CardHeader>
                          <CardTitle>Saved Job Descriptions</CardTitle>
                          <CardDescription>
                            {savedJobs.length} job{savedJobs.length !== 1 ? 's' : ''} in your library
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 px-8">
                          {savedJobs.length === 0 ? (
                            <div className="text-center py-8">
                              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                              <p className="text-muted-foreground mb-4">No job descriptions saved yet</p>
                              <Button variant="outline" onClick={onUploadNew}>
                                <Plus className="w-4 h-4" />
                                Upload Your First Job
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Bulk Actions Header */}
                              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    checked={selectedJobs.size === savedJobs.length && savedJobs.length > 0}
                                    onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                                  />
                                  <span className="text-sm font-medium">
                                    {selectedJobs.size === 0 
                                      ? "Select All" 
                                      : `${selectedJobs.size} of ${savedJobs.length} selected`
                                    }
                                  </span>
                                </div>
                                {selectedJobs.size > 0 && (
                                  <Button 
                                    variant="destructive" 
                                    size="sm"
                                    onClick={() => setShowBulkDeleteDialog(true)}
                                    className="flex items-center gap-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete Selected ({selectedJobs.size})
                                  </Button>
                                )}
                              </div>

                              {savedJobs.map((job) => (
                                <Card 
                                  key={job.id}
                                  className={`transition-all hover:shadow-md cursor-pointer w-full border-l-4 ${
                                    selectedJobs.has(job.id) 
                                      ? 'border-primary bg-gradient-to-r from-primary/10 to-accent/5' 
                                      : 'border-l-primary/30 hover:border-l-primary hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/5'
                                  }`}
                                  onClick={() => setSelectedJob(job)}
                                >
                                  <CardContent className="p-4">
                                    <div className="flex items-center gap-4 w-full">
                                      {/* Selection Checkbox */}
                                      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                          checked={selectedJobs.has(job.id)}
                                          onCheckedChange={(checked) => handleSelectJob(job.id, checked as boolean)}
                                        />
                                      </div>
                                      
                                      {/* Match Score Circle - Smaller with gradient */}
                                      <div className={`w-16 h-16 rounded-full border-2 flex flex-col items-center justify-center flex-shrink-0 shadow-sm ${
                                        job.matchScore >= 80 
                                          ? "text-green-600 border-green-600 bg-gradient-to-br from-green-50 to-green-100" 
                                          : job.matchScore >= 56 
                                          ? "text-yellow-600 border-yellow-600 bg-gradient-to-br from-yellow-50 to-yellow-100" 
                                          : job.matchScore >= 30 
                                          ? "text-orange-600 border-orange-600 bg-gradient-to-br from-orange-50 to-orange-100" 
                                          : "text-red-600 border-red-600 bg-gradient-to-br from-red-50 to-red-100"
                                      }`}>
                                        <div className="text-sm font-bold">{job.matchScore}%</div>
                                      </div>

                                      {/* Job Info - Compact with color accents */}
                                      <div className="flex-1 space-y-2 min-w-0">
                                        <h3 className="font-semibold text-lg leading-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{job.jobTitle}</h3>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                          <Building className="w-4 h-4 text-primary" />
                                          <span className="truncate">{job.companyName}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                          <Calendar className="w-3 h-3 text-accent" />
                                          {formatDate(job.dateCreated)}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs flex-wrap">
                                          <Badge variant="outline" className="text-xs px-2 py-1 h-5 border-primary/30 text-primary">{job.selectedResponsibilities.length} resp.</Badge>
                                          <Badge variant="outline" className="text-xs px-2 py-1 h-5 border-accent/30 text-accent">{job.selectedRequirements.length} req.</Badge>
                                          <Badge variant="outline" className="text-xs px-2 py-1 h-5 border-primary/30 text-primary">{job.selectedSkills.length} skills</Badge>
                                        </div>
                                      </div>

                                      {/* Action Buttons - Smaller with gradient hover */}
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 hover:bg-gradient-to-r hover:from-primary hover:to-accent hover:text-white"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowAnalysisDetail(job);
                                          }}
                                          title="View Detailed Analysis"
                                        >
                                          <BarChart3 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteJob(job.id, job.jobTitle);
                                          }}
                                          disabled={isDeleting === job.id}
                                        >
                                          {isDeleting === job.id ? (
                                            <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
                                          ) : (
                                            <Trash2 className="w-4 h-4" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Detail Modal */}
        {showAnalysisDetail && (
          <JobAnalysisDetail 
            job={showAnalysisDetail} 
            onClose={() => setShowAnalysisDetail(null)} 
          />
        )}

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Selected Jobs</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {selectedJobs.size} job description{selectedJobs.size !== 1 ? 's' : ''}? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isBulkDeleting ? "Deleting..." : "Delete Jobs"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}